import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';

import { AbstractBaseStyleFieldComponent } from '../BaseStyleField/abstract-base-style-field.component';
import { AbstractPageBuilderService } from '../../../../Common/PageBuilder/services/abstract-page-builder.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { CheckboxTypesEnum } from '../../../../Common/Forms/enums/checkbox-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DropdownFieldModel } from '../../../../Common/PageBuilder/models/dropdown-field.model';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { FontAlignmentEnum } from '../../../../Common/Forms/enums/font-alignment.enum';
import { ManageStylesDialogComponent } from '../../../../Common/Styles/components/ManageStylesDialog/manage-styles-dialog.component';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { RadioTypesEnum } from '../../../../Common/Forms/enums/radio-types.enum';
import { SaveStylesDialogComponent } from '../../../../Common/Dialogs/components/SaveStylesDialog/save-styles-dialog.component';
import { SaveTwigTemplateDialogComponent } from '../../../../Common/Dialogs/components/SaveTwigTemplateDialog/save-twig-template-dialog.component';
import { Style } from '../../../../Common/Styles/models/style.model';
import { StylesService } from '../../../../Common/Styles/services/styles.service';
import { StyleStateEnum } from '../../enums/style-state.enum';
import { Template } from '../../../../Common/Styles/models/template.model';
import { TextStyleFieldModel } from '../../../../Common/PageBuilder/models/text-style-field.model';
import { textStyleFontDropdownOptions } from '../../models/text-style-font-dropdown-options.model';
import { textStyleFontSizeDropdownOptions } from '../../models/text-style-font-size-dropdown-options.model';
import { TextStyleModel } from '../../models/text-style.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { DialogIoModel } from '../../../../Core/Dialogs/models/dialog-io.model';
import { BlockDisplayTypeEnum } from '../../enums/block-display-type.enum';
import { AdvancedColorPickerFieldModel, textColorConfig, backgroundColorConfig } from '@Common/PageBuilder/models/advanced-color-picker-field.model';

@Component({
    selector: 'agpr-text-style-field',
    templateUrl: './text-style-field.component.html',
    styleUrls: ['./text-style-field.component.scss'],
    standalone: false
})
export class TextStyleFieldComponent extends AbstractBaseStyleFieldComponent implements OnInit, OnDestroy, OnChanges {
    @Input() declare value: TextStyleModel;

    @Input() declare field: TextStyleFieldModel;
    @Input() hidePadding: boolean = false;
    @Input() showLabel: boolean = true;
    @Input() smallLabel: boolean = false;
    @Input() blockPaddingHeaderClass: string = '';
    @Input() blockPaddingLabelSemibold: boolean = true;
    @Input() layoutVariant: '' | 'headlines' | 'insights' | 'backToTopLinks' | 'sectionHeading' = '';
    @Input() subheadingSpacing: 'default' | 'compact' | 'none' = 'default';

    @Output() changes: EventEmitter<TextStyleModel> = new EventEmitter<TextStyleModel>();

    public colorPickerField: AdvancedColorPickerFieldModel;
    public fontFamilyField: DropdownFieldModel;
    public fontSizeField: DropdownFieldModel;
    public styleStateField: DropdownFieldModel;
    public backgroundColorField: AdvancedColorPickerFieldModel;

    public fontAlignmentEnum = FontAlignmentEnum;
    public checkboxTypesEnum = CheckboxTypesEnum;
    public radioTypesEnum = RadioTypesEnum;
    public messageSeverityEnum = messageSeverityEnum;

    readonly buttonTypes = ButtonTypesEnum;

    protected defaultStyleStateOptions: DropdownOptionModel[];
    protected subscriptions: Subscription[] = [];
    protected styles: Style[];


    constructor(
        protected coreDialogService: CoreDialogService,
        protected stylesService: StylesService,
        public abstractPageBuilderService: AbstractPageBuilderService
    ) {
        super(coreDialogService);
        this.subscriptions.push(
            this.stylesService.all$.subscribe(
                styles => {
                    if (!styles) {
                        return;
                    }

                    this.styles = styles;

                    this.updateStyleOptions();
                    this.updateStyleFromSavedStyle();
                }
            )
        );
    }

    ngOnInit() {
        if (this.block?.displayElement) {
            this.field.allowExpertMode = this.block?.displayElement?.displayType !== BlockDisplayTypeEnum.ONLINE;
        }

        if (!this.value) {
            this.value = new TextStyleModel();
        }

        if (this.field?.defaultStyleStateOptions?.length) {
            this.defaultStyleStateOptions = this.field.defaultStyleStateOptions;
        }

        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }
        this.styleStateField = new DropdownFieldModel(
            null,
            'displayStyleState',
            'PageBuilder.styles.contentBlockStyle',
            '',
            this.defaultStyleStateOptions
        );
        this.updateStyleOptions();

        this.colorPickerField = AdvancedColorPickerFieldModel.create(textColorConfig(true));



        this.fontFamilyField = new DropdownFieldModel(
            null,
            'fontFamily',
            '',
            '',
            textStyleFontDropdownOptions,
            '',
            'TextStyle.fontFamily.label'
        );

        this.fontSizeField = new DropdownFieldModel(
            null,
            'fontSize',
            '',
            '',
            textStyleFontSizeDropdownOptions,
            '',
            'TextStyle.fontSize.label'
        );

        this.backgroundColorField = AdvancedColorPickerFieldModel.create(backgroundColorConfig(true));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('value')) {
            if (!this.value) {
                this.value = new TextStyleModel();
            }

            if (!this.value.fontSize) {
                this.value.fontSize = this.field.defaults.fontSize;
            }
            if (!this.value.color) {
                this.value.color = this.field.defaults.fontColor;
            }
            if (this.value.bold === undefined) {
                this.value.bold = this.field.defaults.bold;
            }
            if (this.value.underlined === undefined) {
                this.value.underlined = this.field.defaults.underline;
            }
            this.updateStyleFromSavedStyle();
        }
    }

    public saveStyle(): void {
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;

        const io: DialogIoModel = {
            inputs: {
                type: this.field.styleType,
                inputStyle: this.styleModel
            },
            outputs: {
                dialogClose: (response) => {
                    if (response) {
                        this.setStyle(response, 'displayStyleState', true);
                    }
                }
            }
        };

        this.coreDialogService.open(SaveStylesDialogComponent, io);
    }

    /**
     * Opens the save-twig-template-dialog after loading the data needed for it
     */
    public saveExpertTemplate(): void {
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;
        this.styleModel.template = this.value.template;
        this.saveTwigTemplateDialogModel.inputs.defaultTemplate = this.abstractPageBuilderService.getDefaultTemplateByBlockType(
            this.abstractPageBuilderService.getBlockTypeEnumFromStyleTypeEnum(this.field.styleType),
            StyleStateEnum.DEFAULT
        );
        // Set the default template if there's not a template on this style already
        if (!this.styleModel.template) {
            this.setTemplate = true;
            this.styleModel.template = new Template();
            this.styleModel.template.template = this.saveTwigTemplateDialogModel.inputs.defaultTemplate;
        }

        this.saveTwigTemplateDialogModel.inputs.type = this.field.styleType;
        this.saveTwigTemplateDialogModel.inputs.inputStyle = this.styleModel;
        const dialogKey = this.coreDialogService.open(SaveTwigTemplateDialogComponent, this.saveTwigTemplateDialogModel);
    }

    public manageStylesButtonClicked(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageStylesDialogComponent, io);
    }

    protected updateStyleFromSavedStyle() {
        if (!this.value || !this.styles || !Number(this.value.styleState)) {
            return;
        }
        const savedStyle: Style = this.styles.find(x => x.id === this.value.styleState as number);
        if (!savedStyle) {
            // the list doesn't contain the id we're looking for, can't load it
            return;
        }
        const textStyleModel = new TextStyleModel(savedStyle.style);
        textStyleModel.styleState = this.value.styleState;
        textStyleModel.displayStyleState = this.value.displayStyleState;
        textStyleModel.template = savedStyle.template;

        this.value = textStyleModel;
    }

    protected updateStyleOptions(): void {
        if (!this.styles || !this.value || !this.value.styleState || !this.defaultStyleStateOptions) {
            return;
        }

        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }

        const styleDropdownOptions = this.styles
            .filter(style => style.type === this.field.styleType)
            .map(
                style => {
                    return {
                        label: style.name,
                        value: style.id
                    };
                }
            );

        this.styleStateField.options = [
            ...this.defaultStyleStateOptions,
            ...styleDropdownOptions
        ];
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
